// 개발요청 상세 — 협의 메모(D4b): 목록 · 쓰기 · 지우기.
// 🔴 본문은 글자로만 그린다(dangerouslySetInnerHTML 금지 · 줄바꿈은 white-space: pre-wrap).
// 🔴 단추는 서버 값으로만 — 쓰기 = item.can.comment(canComment 로 받는다) · 지우기 = 그 행의 can_delete.
//    «AX 내부 메모» 체크만 me.is_ax(isAx 로 받는다)로 보인다 — 서버도 AX 가 아니면 visibility='ax' 를 403 으로 막는다.
import { useEffect, useRef, useState } from 'react';
import { devreqAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS, btn, input as inputStyle } from '../../utils/uiStyles';
import { fmtKstDateTime } from '../../utils/kstTime';
import { errorText, text } from './devreqLabels';

const COMMENT_MAX = 4000;   // = routers/devreq.py _COMMENT_MAX
// 글자 수 «4,000» — 실행 환경의 로캘과 무관하게 같은 꼴.
const commas = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const LOAD_FAILED = '메모를 불러오지 못했습니다.';
const SAVE_FAILED = '메모를 남기지 못했습니다.';
const DELETE_FAILED = '메모를 지우지 못했습니다.';
const AX_BADGE = {
  display: 'inline-block', padding: '1px 7px', borderRadius: '999px', fontSize: '11px', fontWeight: 700,
  background: COLORS.warnSoft, color: COLORS.warn, whiteSpace: 'nowrap',
};

export default function DevRequestComments({ requestId, canComment = false, isAx = false, onChanged }) {
  const [items, setItems] = useState(null);   // null = 불러오는 중
  const [loadError, setLoadError] = useState('');
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const sendingRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setItems(null);
    setLoadError('');
    devreqAPI.comments(requestId)
      .then((r) => { if (alive) setItems(Array.isArray(r?.data) ? r.data : []); })
      .catch(async (err) => {
        const message = await errorText(err, LOAD_FAILED);
        if (alive) setLoadError(message);
      });
    return () => { alive = false; };
  }, [requestId]);

  const canSend = canComment && body.trim() !== '' && !sending;

  const send = async (event) => {
    event.preventDefault();
    if (sendingRef.current || !canSend) return;
    // 일반 메모는 visibility 를 싣지 않는다(서버 기본 all) · AX 내부 메모만 'ax'.
    const payload = isAx && internal ? { body: body.trim(), visibility: 'ax' } : { body: body.trim() };
    sendingRef.current = true;
    setSending(true);
    setError('');
    try {
      const r = await devreqAPI.addComment(requestId, payload);
      if (!aliveRef.current) return;
      if (r?.data) setItems((prev) => [...(prev || []), r.data]);
      setBody('');
      setInternal(false);
      onChanged?.();
    } catch (err) {
      const message = await errorText(err, SAVE_FAILED);
      if (aliveRef.current) setError(message);
    } finally {
      sendingRef.current = false;
      if (aliveRef.current) setSending(false);
    }
  };

  const remove = async (comment) => {
    if (!(await confirmDialog('이 메모를 지울까요?'))) return;
    setError('');
    try {
      await devreqAPI.removeComment(comment.id);
      if (!aliveRef.current) return;
      setItems((prev) => (prev || []).filter((x) => x.id !== comment.id));
      onChanged?.();
    } catch (err) {
      const message = await errorText(err, DELETE_FAILED);
      if (aliveRef.current) setError(message);
    }
  };

  const count = items ? items.length : 0;

  return (
    <section aria-label="메모" style={{ border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '10px 14px', marginBottom: '12px', background: COLORS.surface }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: 700 }}>{`메모 (${count})`}</h3>

      {loadError && <p role="alert" style={{ margin: 0, fontSize: '12px', color: COLORS.dangerText }}>{loadError}</p>}
      {!loadError && items === null && <p role="status" style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>불러오는 중…</p>}
      {items && items.length === 0 && <p style={{ margin: 0, fontSize: '12px', color: COLORS.textSub }}>메모가 없습니다</p>}
      {items && items.length > 0 && (
        <ul aria-label="메모 목록" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map((comment) => (
            <li key={comment.id} data-testid="devreq-comment-row" style={{ padding: '8px 0', borderTop: `1px solid ${COLORS.borderSoft}` }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', fontSize: '12px', color: COLORS.textSub }}>
                <span style={{ fontWeight: 700, color: COLORS.text }}>{text(comment.author_name)}</span>
                <span>{fmtKstDateTime(comment.created_at) || '—'}</span>
                {comment.visibility === 'ax' && <span style={AX_BADGE}>AX 내부</span>}
                {comment.can_delete === true && (
                  <button type="button" aria-label="메모 지우기" onClick={() => remove(comment)}
                    style={{ ...btn('secondary', 'sm'), marginLeft: 'auto' }}>지우기</button>
                )}
              </div>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{comment.body}</p>
            </li>
          ))}
        </ul>
      )}

      {canComment && (
        <form onSubmit={send} style={{ marginTop: '8px' }}>
          <textarea aria-label="메모 내용" value={body} maxLength={COMMENT_MAX} rows={3}
            onChange={(e) => setBody(e.target.value)} placeholder="협의 내용·상세 요구사항"
            style={{ ...inputStyle, resize: 'vertical', fontSize: '13px' }} />
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
            {isAx && (
              <label style={{ fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />AX 내부 메모
              </label>
            )}
            <span style={{ fontSize: '11px', color: COLORS.textSub }}>{`${commas(body.length)}/${commas(COMMENT_MAX)}`}</span>
            <button type="submit" disabled={!canSend} style={{ ...btn('primary', 'sm'), marginLeft: 'auto', opacity: canSend ? 1 : 0.5 }}>
              {sending ? '남기는 중…' : '메모 쓰기'}
            </button>
          </div>
        </form>
      )}
      {error && <p role="alert" style={{ margin: '6px 0 0', fontSize: '12px', color: COLORS.dangerText }}>{error}</p>}
    </section>
  );
}
